import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { publicApi } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { rememberReturnTo } from '../utils/returnTo';

// ?to=open — the link came in an open-shift text (owner, Oct 1 2026): after the
// PIN, go to the Open shifts tab. Only this one fixed value is honoured; any
// other ?to is ignored, so the link can never send anyone off the app.
const OPEN_SHIFTS = '/crew/jobs?tab=open';

/**
 * /join/:token — a new crew member makes their own PIN from the link the
 * office texted them, checks their details, and lands in the crew app signed
 * in (server/services/crewInvite.js).
 */
export default function JoinCrew() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toOpen = params.get('to') === 'open';
  const { loginWithToken, user } = useAuth();
  const { data, error, isLoading } = useQuery(['join', token], () => publicApi.get(`/join/${token}`).then(r => r.data), { retry: false });
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [venmo, setVenmo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const val = (v: string | null, fallback: string) => (v === null ? fallback || '' : v);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setMsg('');
    if (!/^\d{6}$/.test(pin)) { setMsg('Your PIN must be exactly 6 numbers'); return; }
    if (pin !== pin2) { setMsg('The two PINs do not match'); return; }
    setBusy(true);
    try {
      const r = await publicApi.post(`/join/${token}`, {
        pin, pin_confirm: pin2,
        phone: val(phone, data?.phone), email: val(email, data?.email), venmo_handle: val(venmo, data?.venmo_handle),
      });
      loginWithToken(r.data.token, r.data.user);
      const crew = r.data.user?.role === 'crew' || r.data.user?.role === 'crew_viewer';
      navigate(toOpen && crew ? OPEN_SHIFTS : '/', { replace: true });
    } catch (err: any) {
      setMsg(err.response?.data?.message || 'Could not save that — check your connection and try again');
    } finally { setBusy(false); }
  }

  const gone = (error as any)?.response?.data;
  return (
    <div className="min-h-screen bg-gray-50 flex items-start sm:items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Keep Austin Mowed · crew app</p>
        {isLoading ? <p className="text-sm text-gray-500">Opening your link…</p>
          : gone ? (
            <div className="space-y-3">
              <p className="text-base font-semibold text-gray-900">{gone.message || 'That link does not work'}</p>
              {user ? (
                // Already signed in on this phone (they made the PIN from another text): straight in.
                <button onClick={() => navigate(toOpen ? OPEN_SHIFTS : '/')} className="press w-full px-4 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold">{toOpen ? 'See the open shifts' : 'Open the app'}</button>
              ) : (
                <button onClick={() => { if (toOpen) rememberReturnTo(OPEN_SHIFTS); navigate('/login'); }} className="press w-full px-4 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold">Go to sign in</button>
              )}
            </div>
          ) : (
            <form onSubmit={save} className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Hi {data?.first_name || 'there'} 👋</h1>
                <p className="text-sm text-gray-600">Pick the 6-number PIN you'll sign in with. Only you and the office will know it.</p>
                {toOpen && <p className="text-sm text-gray-600 mt-1">Then you go straight to the open shifts to claim yours.</p>}
              </div>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Your PIN</span>
                <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={pin} autoFocus
                  onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input w-full py-2.5 text-lg font-mono tracking-[0.4em]" placeholder="••••••" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Type it again</span>
                <input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={pin2}
                  onChange={e => setPin2(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input w-full py-2.5 text-lg font-mono tracking-[0.4em]" placeholder="••••••" />
              </label>
              <div className="border-t border-gray-100 pt-3 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your details</p>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600">Phone</span>
                  <input type="tel" value={val(phone, data?.phone)} onChange={e => setPhone(e.target.value)} className="input w-full py-2" placeholder="(512) 555-0100" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600">Email</span>
                  <input type="email" value={val(email, data?.email)} onChange={e => setEmail(e.target.value)} className="input w-full py-2" placeholder="you@example.com" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-600">Venmo <span className="text-gray-500 font-normal">(how you get paid)</span></span>
                  <input value={val(venmo, data?.venmo_handle)} onChange={e => setVenmo(e.target.value)} className="input w-full py-2" placeholder="@your-venmo" />
                </label>
              </div>
              {msg && <p className="text-sm text-red-600">{msg}</p>}
              <button type="submit" disabled={busy} className="press w-full px-4 py-3 rounded-xl bg-brand-600 text-black text-base font-semibold disabled:opacity-50">
                {busy ? 'Saving…' : 'Save and sign in'}
              </button>
            </form>
          )}
      </div>
    </div>
  );
}
